import React from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import ScrollableTooltipContent from '@sharedCR/common/ScrollableTooltipContent';
import PropTypes from 'prop-types';

import { fixedTwoFractionDigitsCurrencyOptions, LienPriorityType } from 'constants/loanApplication';
import { PendingChangeKind } from 'constants/pendingChanges';
import useModifiedFieldPendingChangeAuthors from 'hooks/useModifiedFieldPendingChangeAuthors';
import { formatNumberToCurrencyString } from 'utils/number';
import { getModifiedFieldChangeKind } from 'utils/pendingChangeKind';

import { selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary } from 'store/loanApplications/selectors';

import { getPropertyExpensesHoverData } from '../PropertyAndLoanSummary/PropertyAndLoanSummary.utils';

import { PITI_LABEL, PITI_PENDING_CHANGES_KEY, rows } from './PitiData.constants';
import {
  commonPendingChangesStyles,
  footerCellStyles,
  footerStyles,
  getTableCellPendingChangesStyles,
  tableBodyStyles,
  tableContainerStyles,
} from './PitiData.styles';

const PitiData = ({ data: rawData }) => {
  const isAdditionalLoan = useSelector(selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary);
  const isSubordinateLien = isAdditionalLoan ?? rawData.lienPriorityType !== LienPriorityType.FIRST;
  const getModifiedFieldAuthors = useModifiedFieldPendingChangeAuthors();

  const data = getPropertyExpensesHoverData(rawData, isAdditionalLoan);
  const pitiChangeKind = getModifiedFieldChangeKind(data, PITI_PENDING_CHANGES_KEY);

  return (
    <ScrollableTooltipContent
      footer={
        <Box sx={footerStyles}>
          <Stack gap={1.5} sx={footerCellStyles} textAlign="left">
            {PITI_LABEL}:
          </Stack>

          <Box sx={footerCellStyles} textAlign="right">
            <PendingChangeWrapper
              isSmall
              authors={getModifiedFieldAuthors(data.modifiedFieldDetails, PITI_PENDING_CHANGES_KEY)}
              changeKind={pitiChangeKind}
              fieldLabel={PITI_LABEL}
              sx={commonPendingChangesStyles}
            >
              <Typography data-truncated className="truncate value" variant="regular14">
                {formatNumberToCurrencyString(
                  data.piti ?? 0,
                  fixedTwoFractionDigitsCurrencyOptions,
                )}
              </Typography>
            </PendingChangeWrapper>
          </Box>
        </Box>
      }
    >
      <TableContainer className="table-container" sx={tableContainerStyles}>
        <Table className="table">
          <TableBody className="table-body" sx={tableBodyStyles}>
            {rows.map(({ label, valueKey, pendingChangesKey, checkIsHidden }) => {
              const value = data[valueKey];
              const changeKind = getModifiedFieldChangeKind(data, pendingChangesKey);

              if (checkIsHidden?.(isSubordinateLien) || !value) {
                return null;
              }

              return (
                <TableRow className="table-row" key={valueKey}>
                  <TableCell align="left" className="table-cell">
                    {label}
                  </TableCell>
                  <TableCell align="right" className="table-cell">
                    <PendingChangeWrapper
                      isSmall
                      authors={getModifiedFieldAuthors(
                        data.modifiedFieldDetails,
                        pendingChangesKey,
                      )}
                      changeKind={changeKind}
                      fieldLabel={label}
                      sx={getTableCellPendingChangesStyles({
                        isModified: changeKind === PendingChangeKind.DIRECT,
                      })}
                    >
                      <Typography data-truncated className="truncate value" variant="regular14">
                        {formatNumberToCurrencyString(
                          value ?? 0,
                          fixedTwoFractionDigitsCurrencyOptions,
                        )}
                      </Typography>
                    </PendingChangeWrapper>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </ScrollableTooltipContent>
  );
};

PitiData.propTypes = {
  data: PropTypes.object.isRequired,
};

export default PitiData;
